import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, FormControl, RadioGroup, FormControlLabel, Radio, Typography } from '@material-ui/core';
import Popup from 'components/popup/popup';
import CustomTextField from 'components/Input/CustomTextField';
import HospitalOrInstitutions from 'components/Global/HospitalOrInstitutions';
import CustomButton from 'components/CustomInput/Button';
import useDialog from 'components/ConfirmDialog/useDialog';
import { EDIT_EMAIL_CONFIRM, NO_INPUT_DATA_MODIFIED } from 'utility/frontendMessage';
import API from 'api/call';
import PasswordTextField from 'components/Input/PasswordTextField';

const useStyles = makeStyles((theme) => ({
    container: {
        flex: 1,
        overflow: 'auto',
    },
    title: {
        fontWeight: 'bold',
    },
    item: {
        marginTop: theme.spacing(2),
    },
    radioGroup: {
        flexDirection: 'row',
        flexWrap: 'nowrap',
    },
}));

export default function CreateEditReviewer({ rowData, editReviewer, createReviewer, setOpenPopup, openPopup }) {
    const classes = useStyles();
    const showDialog = useDialog();

    const [forceShowError, setForceShowError] = useState(false);
    const [forceShowEmialError, setForceShowEmialError] = useState(false);
    const isUserEdit = rowData ? true : false;

    const [email, setEmail] = useState(rowData ? rowData.email : '');
    const [firstName, setFirstName] = useState(rowData ? rowData.firstName : '');
    const [surname, setSurname] = useState(rowData ? rowData.surname : '');
    const [title, setTitle] = useState(rowData ? rowData.title : '');
    const [gender, setGender] = useState(rowData ? rowData.gender : '');
    const [department, setDepartment] = useState(rowData ? rowData.department : '');
    const [post, setPost] = useState(rowData ? rowData.post : '');
    const [institution, setInstitution] = useState(rowData ? rowData.institution : '');
    const [background, setBackground] = useState(rowData ? rowData.background : '');
    const [independent, setIndependent] = useState(rowData ? (rowData.independent ? 1 : 0) : null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [isProfileLoaded, setProfileLoaded] = useState(false);
    const [canEditEmail, setCanEditEmail] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isExistingUser, setIsExistingUser] = useState(false);

    const [id, setId] = useState(rowData ? rowData.id : null);

    const disable = canEditEmail || isLoading || !isProfileLoaded;
    const isDisabled = isUserEdit ? false : disable;

    // pre-fill data: user can not edit the firstName,surname,title,institution
    const canEditPreFill = false;
    let isPreFillDisabled = true;
    if (isUserEdit) {
        // Edit reviewer
        if (canEditPreFill) {
            isPreFillDisabled = false;
        } else {
            isPreFillDisabled = true;
        }
    } else {
        // Create reviewer
        if (canEditPreFill) {
            isPreFillDisabled = disable || false;
        } else {
            isPreFillDisabled = isExistingUser || disable;
        }
    }

    const isDisabledEmail = isUserEdit || isLoading || !canEditEmail;

    const createFormValid = {
        email: true,
        firstName: true,
        surname: true,
        title: true,
        gender: false,
        department: true,
        post: true,
        institution: true,
        background: false,
        independent: false,
        password: true,
        confirmPassword: true,
    };

    const editFormValid = {
        email: true,
        firstName: true,
        surname: true,
        title: true,
        gender: true,
        department: true,
        post: true,
        institution: true,
        background: true,
        independent: true,
    };

    const formValid = useRef(isUserEdit ? editFormValid : createFormValid);

    const updateFormValid = (key, value) => {
        formValid.current[key] = value;
    };

    useEffect(() => {
        if (gender === null || gender === undefined) {
            updateFormValid('gender', false);
        }

        if (background === null || background === undefined) {
            updateFormValid('background', false);
        }

        if (independent === null || independent === undefined) {
            updateFormValid('independent', false);
        }
    }, [gender, background, independent]);

    const loadReviewerDetail = () => {
        API.reviewer
            .getReviewerDetail(email)
            .then((result) => {
                const res = result.data.result;
                if (res) {
                    // Existing User
                    setIsExistingUser(true);
                    if (!isUserEdit) {
                        delete formValid.current.password;
                        delete formValid.current.confirmPassword;
                    }

                    // set default data
                    setEmail(res.email);
                    setFirstName(res.firstName);
                    setSurname(res.surname);
                    setTitle(res.title);
                    setInstitution(res.institution);
                    setGender(res.gender ?? '');
                    setDepartment(res.department ?? '');
                    setPost(res.post ?? '');
                    setBackground(res.background ?? '');
                    setIndependent(res.independent !== null ? (res.independent ? 1 : 0) : '');

                    updateFormValid('gender', res.gender ? true : false);
                    updateFormValid('background', res.background ? true : false);
                    updateFormValid('independent', res.independent !== null ? true : false);

                    setId(res.id);

                    if (res.message) {
                        showDialog({
                            description: res.message,
                            disableCancelButton: true,
                        });
                    }
                } else {
                    // not Existing User
                    setIsExistingUser(false);
                    if (!isUserEdit) {
                        formValid.current = {
                            ...formValid.current,
                            password: true,
                            confirmPassword: true,
                        };
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
                setCanEditEmail(false);
                setProfileLoaded(true);
            });
    };

    const handleLoadProfile = async () => {
        if (canEditEmail) {
            if (!formValid.current.email) {
                setForceShowEmialError(true);
                return;
            }

            setIsLoading(true);
            loadReviewerDetail();
        } else {
            const isConfirm = await showDialog({
                description: EDIT_EMAIL_CONFIRM,
            });
            if (isConfirm) {
                // setCanEditEmail(true);
                reset();
            }
        }
    };

    const checkFormValid = () => {
        console.log('%c 115738 createEdit.js formValid', 'background: green', formValid);

        let valid = true;
        for (let key in formValid.current) {
            if (!formValid.current[key]) {
                valid = false;
                break;
            }
        }
        return valid;
    };

    const getEditData = () => {
        let data = { id: id };
        if (!isPreFillDisabled) {
            if (firstName !== rowData?.firstName) {
                data.firstName = firstName;
            }
            if (surname !== rowData?.surname) {
                data.surname = surname;
            }
            if (title !== rowData?.title) {
                data.title = title;
            }
            if (institution !== rowData?.institution) {
                data.institution = institution;
            }
        }

        if (gender !== rowData?.gender) {
            data.gender = gender;
        }
        if (department !== rowData?.department) {
            data.department = department;
        }
        if (post !== rowData?.post) {
            data.post = post;
        }
        if (background !== rowData?.background) {
            data.background = background;
        }
        const newIndependent = independent ? true : false;
        if (newIndependent !== rowData?.independent) {
            data.independent = newIndependent;
        }

        return data;
    };

    const submit = () => {
        if (!checkFormValid()) {
            setForceShowError(true);
            setForceShowEmialError(true);
            return;
        }

        if (isUserEdit || isExistingUser) {
            const data = getEditData();
            if (Object.keys(data).length === 1) {
                showDialog({
                    description: NO_INPUT_DATA_MODIFIED,
                    disableCancelButton: true,
                });
                return;
            }
            editReviewer(data, isUserEdit);
        } else {
            let data = {
                email,
                gender,
                department,
                post,
                background,
                independent: independent ? true : false,
            };

            if (!isExistingUser) {
                data = {
                    ...data,
                    firstName,
                    surname,
                    title,
                    institution,
                    password,
                    confirmPassword,
                };
            }
            createReviewer(data);
        }
    };
    const cancel = () => {
        setOpenPopup(false);
    };
    const reset = () => {
        setCanEditEmail(true);
        setEmail(rowData ? rowData.email : '');
        setFirstName(rowData ? rowData.firstName : '');
        setSurname(rowData ? rowData.surname : '');
        setTitle(rowData ? rowData.title : '');
        setGender(rowData ? rowData.gender : '');
        setDepartment(rowData ? rowData.department : '');
        setPost(rowData ? rowData.post : '');
        setInstitution(rowData ? rowData.institution : '');
        setBackground(rowData ? rowData.background : '');
        setIndependent(rowData ? (rowData.independent ? 1 : 0) : '');
    };
    return (
        <Popup
            width="800px"
            title={`${isUserEdit ? 'Edit' : 'Create'} Reviewer Account`}
            open={openPopup}
            footerActions={[
                { label: 'Cancel', clicked: cancel, color: 'default' },
                { label: 'Reset', clicked: reset },
                { label: 'Confirm', clicked: submit },
            ]}
            closePopup={cancel}
        >
            <div style={{ width: '100%' }}>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Email:
                    </Grid>
                    <Grid container item xs={8}>
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div style={{ flex: 1 }}>
                                <CustomTextField
                                    label="Email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    isNoSpace
                                    showErrorWhenOnBlur
                                    validators={['isRequired', 'isEmail', 'disableChinese']}
                                    isValidValue={(valid) => updateFormValid('email', valid)}
                                    forceErrorDisplay={forceShowEmialError}
                                    maxLength={255}
                                    disabled={isDisabledEmail}
                                />
                            </div>
                            {!isUserEdit && (
                                <div style={{ paddingLeft: 10 }}>
                                    <CustomButton
                                        style={{ height: 38 }}
                                        onClick={handleLoadProfile}
                                        loading={isLoading}
                                    >
                                        {canEditEmail ? 'Load Profile' : 'Edit'}
                                    </CustomButton>
                                </div>
                            )}
                        </div>
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" className={classes.item}>
                    <Grid container item xs={6} alignItems="center">
                        <Grid container item xs={4} className={classes.title}>
                            First Name:
                        </Grid>
                        <Grid container item xs={6}>
                            <CustomTextField
                                label="First Name"
                                name="firstName"
                                value={firstName}
                                disabled={isPreFillDisabled}
                                onChange={(e) => setFirstName(e.target.value)}
                                validators={['isRequired', 'disableChinese']}
                                isValidValue={(valid) => updateFormValid('firstName', valid)}
                                forceErrorDisplay={forceShowError}
                                maxLength={255}
                            />
                        </Grid>
                    </Grid>
                    <Grid container item xs={6} alignItems="center">
                        <Grid container item xs={2} className={classes.title}>
                            Surname:
                        </Grid>
                        <Grid container item xs={6}>
                            <CustomTextField
                                label="Surname"
                                name="surname"
                                disabled={isPreFillDisabled}
                                value={surname}
                                onChange={(e) => setSurname(e.target.value)}
                                validators={['isRequired', 'disableChinese']}
                                isValidValue={(valid) => updateFormValid('surname', valid)}
                                forceErrorDisplay={forceShowError}
                                maxLength={255}
                            />
                        </Grid>
                    </Grid>
                </Grid>

                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Title:
                    </Grid>
                    <Grid container item xs={8}>
                        <CustomTextField
                            label="Title"
                            name="title"
                            disabled={isPreFillDisabled}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            validators={['isRequired', 'disableChinese']}
                            isValidValue={(valid) => updateFormValid('title', valid)}
                            forceErrorDisplay={forceShowError}
                            maxLength={255}
                        />
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Gender:
                    </Grid>
                    <Grid container item xs={8}>
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                aria-label="gender"
                                name="gender"
                                className={classes.radioGroup}
                                value={gender}
                                onChange={(e) => {
                                    updateFormValid('gender', true);
                                    setGender(e.target.value);
                                }}
                            >
                                <FormControlLabel
                                    value="F"
                                    control={<Radio color="primary" disabled={isDisabled} />}
                                    label="Female"
                                />
                                <FormControlLabel
                                    value="M"
                                    control={<Radio disabled={isDisabled} color="primary" />}
                                    label="Male"
                                />
                            </RadioGroup>
                        </FormControl>
                        {forceShowError && !gender && (
                            <Grid container style={{ marginTop: 8 }}>
                                <Typography color={'error'}>At least one of the option should be selected.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Department:
                    </Grid>
                    <Grid container item xs={8}>
                        <CustomTextField
                            label="Department"
                            name="department"
                            disabled={isDisabled}
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            validators={['isRequired', 'disableChinese']}
                            isValidValue={(valid) => updateFormValid('department', valid)}
                            forceErrorDisplay={forceShowError}
                            maxLength={255}
                        />
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Post:
                    </Grid>
                    <Grid container item xs={8}>
                        <CustomTextField
                            label="Post"
                            name="post"
                            disabled={isDisabled}
                            value={post}
                            onChange={(e) => setPost(e.target.value)}
                            validators={['isRequired', 'disableChinese']}
                            isValidValue={(valid) => updateFormValid('post', valid)}
                            forceErrorDisplay={forceShowError}
                            maxLength={255}
                        />
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Institution:
                    </Grid>
                    <Grid container item xs={8}>
                        <HospitalOrInstitutions
                            isAuthorizedEmail={false}
                            hospitalOrInstitution={institution}
                            setHospitalOrInstitution={(e) => setInstitution(e.target.value)}
                            disabled={isPreFillDisabled}
                            validators={['isRequired', 'disableChinese']}
                            isValidValue={(valid) => updateFormValid('post', valid)}
                            forceErrorDisplay={forceShowError}
                            maxLength={255}
                        />
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Background:
                    </Grid>
                    <Grid container item xs={8}>
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                aria-label="background"
                                name="background"
                                className={classes.radioGroup}
                                value={background}
                                onChange={(e) => {
                                    updateFormValid('background', true);
                                    setBackground(e.target.value);
                                }}
                            >
                                <FormControlLabel
                                    value="Scientific"
                                    control={<Radio color="primary" disabled={isDisabled} />}
                                    label="Scientific"
                                />
                                <FormControlLabel
                                    value="Non-Scientific"
                                    control={<Radio color="primary" disabled={isDisabled} />}
                                    label="Non-scientific"
                                />
                            </RadioGroup>
                        </FormControl>
                        {forceShowError && !background && (
                            <Grid container style={{ marginTop: 8 }}>
                                <Typography color={'error'}>At least one of the option should be selected.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={2} className={classes.title}>
                        Independent:
                    </Grid>
                    <Grid container item xs={8}>
                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                aria-label="independent"
                                name="independent"
                                className={classes.radioGroup}
                                value={independent}
                                onChange={(e) => {
                                    updateFormValid('independent', true);
                                    setIndependent(Number(e.target.value));
                                }}
                            >
                                <FormControlLabel
                                    value={1}
                                    control={<Radio color="primary" disabled={isDisabled} />}
                                    label="Yes"
                                />
                                <FormControlLabel
                                    value={0}
                                    control={<Radio color="primary" disabled={isDisabled} />}
                                    label="No"
                                />
                            </RadioGroup>
                        </FormControl>
                        {forceShowError && (independent === null || independent === undefined) && (
                            <Grid container style={{ marginTop: 8 }}>
                                <Typography color={'error'}>At least one of the option should be selected.</Typography>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                {!isUserEdit && !isExistingUser && (
                    <>
                        <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                            <Grid container item xs={2} className={classes.title}>
                                Password:
                            </Grid>
                            <Grid container item xs={8}>
                                <PasswordTextField
                                    label="Password"
                                    name="password"
                                    disabled={isDisabled}
                                    value={password}
                                    handleChange={(e) => setPassword(e.target.value)}
                                    validators={['isRequired', 'disableChinese', 'checkPassword']}
                                    isValidValue={(valid) => updateFormValid('password', valid)}
                                    forceErrorDisplay={forceShowError}
                                    maxLength={255}
                                />
                            </Grid>
                        </Grid>
                        <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                            <Grid container item xs={2} className={classes.title}>
                                Confirm Password:
                            </Grid>
                            <Grid container item xs={8}>
                                <PasswordTextField
                                    label="Confirm password"
                                    name="confirmPassword"
                                    disabled={isDisabled}
                                    value={confirmPassword}
                                    handleChange={(e) => setConfirmPassword(e.target.value)}
                                    validators={['isRequired', 'disableChinese', 'checkConfirmPassword']}
                                    isValidValue={(valid) => updateFormValid('confirmPassword', valid)}
                                    forceErrorDisplay={forceShowError}
                                    maxLength={255}
                                    confirmPasswordNotMatchPassword={confirmPassword !== password}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}
            </div>
        </Popup>
    );
}
